import {
  DEFAULT_VIEWPORT,
  EMPTY_VIEWPORT_SIZE,
  type MarqueeState,
  type Store,
  type ViewportSize,
  type ViewportState,
  createLens,
  createStore,
  deriveStore,
} from '@frontend/viewer-core';

import { MAPPING_HISTORY_CAPACITY } from '../constants';
import { createAccentSourceSlice } from './accentBuilder';
import { createHistoryStore, type HistoryStore } from './historyStore';
import {
  createHiddenLayersSlice,
  createHoveredLayerSlice,
  createLayerController,
} from './layerController';
import {
  createRoleBatchBuilder,
  createRoleOverlaySourceSlice,
} from './mappingAccents';
import {
  createHoveredRoleSlice,
  createMappingController,
  createMappingSlice,
  createMappingSnapshotSlice,
} from './mappingController';
import {
  assignmentsEqual,
  changedEntityKeys,
  entityKeyLayers,
  selectionLayers,
} from './mappingModel';
import { createMarqueeController } from './marqueeController';
import { createMaterialFillBuilder } from './materialFill';
import {
  createMeasureController,
  createMeasureSlice,
} from './measureController';
import { createModeController, createModeSlice } from './modeController';
import {
  createOverlayController,
  createOverlaysSlice,
} from './overlayController';
import { createPointerTracker } from './pointerTracker';
import { createRendererBinding } from './rendererBinding';
import { createRoleChromeController } from './roleChromeController';
import {
  createModelStore,
  createSceneLoader,
  createStatusStore,
} from './sceneLoader';
import {
  createHoveredSlice,
  createSelectionController,
  createSelectionSlice,
} from './selectionController';
import { createSnapResolver } from './snapResolver';
import { createSurfaceBinding } from './surfaceBinding';
import { createToolController, createToolSlice } from './toolController';
import {
  EMPTY_POINTER,
  INITIAL_LOAD_STATE,
  INITIAL_SESSION_STATE,
  type DxfSceneLoadState,
  type DxfViewerSession,
  type MappingSnapshot,
  type MarqueePreview,
  type PointerState,
} from './types';
import { createViewportController } from './viewportController';

/**
 * Creates a DXF viewer session.
 *
 * One session per provider, created once and never replaced — its identity is what
 * makes `DxfViewerContext` inert. The previous design built a fresh handle object on
 * every viewport change and pushed it into provider state, so a single wheel notch
 * re-rendered the whole viewer page twice; nothing here is allowed to reach React
 * unless a person caused it.
 *
 * The session owns two stores with different audiences:
 *
 * - `state` — discrete, React-facing interaction state (selection, tools, …).
 * - `load` — drawing fetch / derive lifecycle (`idle` | `loading` | `ready` | `error`).
 * - `viewport` (and its `size`) — frame rate, subscribed to by the renderer and the
 *   text layer only.
 *
 * Keeping those apart is the whole point; putting the viewport in `state` would
 * restore the original problem exactly.
 *
 * This function is deliberately just wiring. `state` itself is never handed to a
 * controller — each field it holds is carved off with `createLens` into its own
 * `Store`, defined in and exported by whichever controller module owns that field
 * (`createMeasureSlice` next to `createMeasureController`, and so on), and only
 * that narrow slice is passed down. A controller that needs to *read* a field it
 * doesn't own (selection needing to know which layers are hidden, say) gets a bare
 * getter function instead of the slice itself, so it can't write what it doesn't
 * own. What's left here is creating the stores and slices, handing each controller
 * exactly what it declared it needs, and assembling the result.
 */
export const createDxfViewerSession = (): DxfViewerSession => {
  const state = createStore(INITIAL_SESSION_STATE);
  const loadStore: Store<DxfSceneLoadState> = createStore(INITIAL_LOAD_STATE);
  const modelStore = createModelStore(loadStore);
  const statusStore = createStatusStore(loadStore);
  const viewportStore: Store<ViewportState> = createStore(DEFAULT_VIEWPORT);
  const sizeStore: Store<ViewportSize> = createStore(EMPTY_VIEWPORT_SIZE);
  const marqueeStore: Store<MarqueeState | null> = createStore(null);
  const previewStore: Store<MarqueePreview | null> = createStore(null);
  const pointerStore: Store<PointerState> = createStore(EMPTY_POINTER);

  // One lens per field of `state`. Each is defined next to the controller that
  // owns it; `isPaused` has no controller of its own, so its lens lives here.
  const hiddenLayers = createHiddenLayersSlice(state);
  const hoveredLayer = createHoveredLayerSlice(state);
  const selectionSlice = createSelectionSlice(state);
  const hoveredSlice = createHoveredSlice(state);
  const measureSlice = createMeasureSlice(state);
  const overlaysSlice = createOverlaysSlice(state);
  const toolSlice = createToolSlice(state);
  const modeSlice = createModeSlice(state);
  const mappingSlice = createMappingSlice(state);
  const hoveredRoleSlice = createHoveredRoleSlice(state);
  // Selection and draft as one writable unit, and the undo stack over it. Created
  // before the controllers because `layers` needs its suppression scope.
  const mappingSnapshot = createMappingSnapshotSlice(state);
  const recordedHistory = createHistoryStore(mappingSnapshot, {
    capacity: MAPPING_HISTORY_CAPACITY,
  });
  const isPausedSlice = createLens(
    state,
    (current) => current.isPaused,
    (current, isPaused) => ({ ...current, isPaused }),
  );
  /** The part file this viewer shell is bound to — set by `DXFViewer` from its prop. */
  const partFileUuidStore = createStore<string | null>(null);
  const accentSource = createAccentSourceSlice(state);
  const roleOverlaySource = createRoleOverlaySourceSlice(state);

  const getModel = modelStore.getState;

  const viewport = createViewportController({
    viewportStore,
    sizeStore,
    getModel,
    getHiddenLayers: hiddenLayers.getState,
  });
  const layers = createLayerController({
    hiddenLayers,
    hoveredLayer,
    selection: selectionSlice,
    getModel,
    viewport,
    withoutRecording: recordedHistory.withoutRecording,
  });
  const selection = createSelectionController({
    selection: selectionSlice,
    hovered: hoveredSlice,
    getHiddenLayers: hiddenLayers.getState,
    getModel,
    viewport,
  });
  const resolveSnap = createSnapResolver({
    viewport,
    getModel,
    getTool: toolSlice.getState,
    getHiddenLayers: hiddenLayers.getState,
  });
  const pointerTracker = createPointerTracker({
    pointerStore,
    resolve: resolveSnap,
  });
  const measure = createMeasureController({
    measure: measureSlice,
    resolve: resolveSnap,
  });
  const overlays = createOverlayController({ overlays: overlaysSlice });
  const marqueeController = createMarqueeController({
    marqueeStore,
    previewStore,
    model: getModel,
    getHiddenLayers: hiddenLayers.getState,
    selection,
    screenToWorld: viewport.screenToWorld,
  });
  const roleChrome = createRoleChromeController({
    mode: modeSlice,
    selection: selectionSlice,
  });
  const tools = createToolController({
    tool: toolSlice,
    selection,
    measure,
    marqueeController,
    pointerTracker,
    roleChrome,
  });
  const mapping = createMappingController({
    mapping: mappingSlice,
    snapshot: mappingSnapshot,
    selection: selectionSlice,
    hoveredRole: hoveredRoleSlice,
    getModel,
    getHiddenLayers: hiddenLayers.getState,
  });
  /**
   * Shows any layer an undo or redo has just brought back into play.
   *
   * A step is only worth taking if its result is visible. Hiding a layer prunes the
   * live selection but leaves earlier steps holding it, so walking back to one can
   * restore a selection — or a role change — that sits on a layer since hidden.
   * Revealing it is the alternative to silently dropping the step or to pruning the
   * past, and it is the one that keeps undo honest: what changed is what you see.
   *
   * Deliberately one-way. Visibility is not part of a snapshot, so a layer is never
   * re-hidden by stepping the other way — that would turn undo into a second,
   * confusing control over what is on screen.
   */
  const revealAffectedLayers = (
    before: MappingSnapshot,
    after: MappingSnapshot,
  ) => {
    const model = getModel();
    if (!model) {
      return;
    }
    const affected = new Set([
      ...selectionLayers(after.selection, model),
      ...entityKeyLayers(changedEntityKeys(before.draft, after.draft), model),
    ]);
    affected.forEach((layerIndex) => layers.setVisible(layerIndex, true));
  };

  /** Wraps a history move so whatever it restored is on screen afterwards. */
  const withReveal = (move: () => void) => () => {
    const before = mappingSnapshot.getState();
    move();
    revealAffectedLayers(before, mappingSnapshot.getState());
  };

  const mappingHistory: HistoryStore = {
    ...recordedHistory,
    undo: withReveal(recordedHistory.undo),
    redo: withReveal(recordedHistory.redo),
  };

  const mode = createModeController({
    mode: modeSlice,
    tool: toolSlice,
    mapping,
    measure,
    overlays,
    marqueeController,
    pointerTracker,
    roleChrome,
    history: mappingHistory,
    getModel,
  });
  // Derived rather than stored: compares draft to the entry baseline by value so
  // a round-trip edit that lands on the same roles is not treated as unsaved.
  const hasUnsavedMapping = deriveStore(mappingSlice, (current) => {
    const { draft, baseline } = current;
    if (draft === null || baseline === null) {
      return false;
    }
    return !assignmentsEqual(draft, baseline);
  });
  const loadSceneInto = createSceneLoader({
    load: loadStore,
    viewport,
    layers,
    selection,
    measure,
    marqueeController,
    pointerTracker,
    mode,
    mapping,
    history: mappingHistory,
    tools,
  });
  const bindSurface = createSurfaceBinding({
    sizeStore,
    model: modelStore,
    getTool: toolSlice.getState,
    tools,
    mapping,
    roleChrome,
    getMode: modeSlice.getState,
    getIsPaused: isPausedSlice.getState,
    history: mappingHistory,
    viewport,
    selection,
    marqueeController,
    marqueePreview: {
      getState: previewStore.getState,
      subscribe: previewStore.subscribe,
    },
    measure,
    pointerTracker,
  });
  const buildRoleBatches = createRoleBatchBuilder();
  const buildMaterialFill = createMaterialFillBuilder();
  const bindRenderer = createRendererBinding({
    viewportStore,
    sizeStore,
    modelStore,
    hiddenLayers,
    getSelection: selectionSlice.getState,
    getHovered: hoveredSlice.getState,
    getHoveredLayer: hoveredLayer.getState,
    getHoveredRole: hoveredRoleSlice.getState,
    overlays: overlaysSlice,
    accentSource,
    preview: previewStore,
    roleOverlaySource,
    getRoleIndex: mapping.roleIndex,
    getHasMapping: () => mapping.effective() !== null,
    buildRoleBatches,
    buildMaterialFill,
  });

  // Disposal state values
  let detachSurface: (() => void) | null = null;
  let isDisposed = false;

  return {
    state: { getState: state.getState, subscribe: state.subscribe },
    load: { getState: loadStore.getState, subscribe: loadStore.subscribe },
    status: {
      getState: statusStore.getState,
      subscribe: statusStore.subscribe,
    },
    viewport,
    model: { getState: modelStore.getState, subscribe: modelStore.subscribe },
    layers,
    selection,
    tools,
    mode,
    mapping,
    mappingHistory,
    hasUnsavedMapping,
    measure,
    overlays,
    roleChrome,
    marqueeController,
    marquee: {
      getState: marqueeStore.getState,
      subscribe: marqueeStore.subscribe,
    },
    marqueePreview: {
      getState: previewStore.getState,
      subscribe: previewStore.subscribe,
    },
    pointer: pointerTracker.store,
    hiddenLayers,
    hoveredLayer,

    partFileUuid: partFileUuidStore,

    setPartFileUuid(partFileUuid) {
      partFileUuidStore.setState((previous) =>
        previous === partFileUuid ? previous : partFileUuid,
      );
    },

    attachSurface(host) {
      detachSurface?.();
      const detach = bindSurface(host);
      detachSurface = () => {
        detach();
        detachSurface = null;
      };
      return detachSurface;
    },

    attachRenderer: bindRenderer,

    loadScene(input) {
      if (isDisposed) {
        return;
      }
      loadSceneInto(input);
    },

    setPaused(isPaused) {
      isPausedSlice.setState((previous) =>
        previous === isPaused ? previous : isPaused,
      );
    },

    dispose() {
      isDisposed = true;
      detachSurface?.();
      // Before the load reset, so the teardown does not get recorded as a step.
      mappingHistory.detach();
      loadStore.setState(() => INITIAL_LOAD_STATE);
    },
  };
};
